import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, User, Heart, Sun, Moon, X, Scroll, Hand } from 'lucide-react';
import { useSettingsStore } from '../lib/store';

export default function FloatingMenu() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const {
    themePreference,
    setThemePreference,
    navigationMode,
    setNavigationMode,
    gestureSensitivity,
    setGestureSensitivity,
  } = useSettingsStore();

  const close = () => setIsOpen(false);

  const navItems = [
    {
      icon: BookOpen,
      label: 'Rêves',
      action: () => { navigate('/dreams'); close(); },
    },
    {
      icon: User,
      label: 'Profil',
      action: () => { navigate('/settings'); close(); },
    },
    {
      icon: Heart,
      label: 'Favoris',
      action: () => { navigate('/dreams'); close(); },
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40"
            onClick={close}
          />
        )}
      </AnimatePresence>

      {/* Menu sheet */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="sheet"
            initial={{ y: 24, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 16, opacity: 0, scale: 0.97 }}
            transition={{ type: 'spring', damping: 30, stiffness: 350 }}
            className="fixed left-4 right-4 z-50 rounded-3xl overflow-hidden"
            style={{
              bottom: '100px',
              background: 'rgba(12,12,16,0.88)',
              backdropFilter: 'blur(28px)',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 space-y-3">

              {/* Navigation items */}
              <div className="grid grid-cols-3 gap-2">
                {navItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className="flex flex-col items-center gap-2 py-4 px-2 rounded-2xl transition-colors"
                    style={{ background: 'rgba(255,255,255,0.06)' }}
                  >
                    <item.icon className="w-5 h-5 text-white/60" />
                    <span className="text-[11px] text-white/40">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* Separator */}
              <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

              {/* Theme + Navigation on same row */}
              <div className="flex gap-2">
                {/* Light / Dark */}
                <div
                  className="flex items-center gap-1 p-1 rounded-2xl flex-1"
                  style={{ background: 'rgba(255,255,255,0.05)' }}
                >
                  <button
                    onClick={() => setThemePreference('light')}
                    className="flex-1 flex items-center justify-center py-2.5 rounded-xl transition-all"
                    style={{
                      background: themePreference === 'light' ? 'rgba(255,255,255,0.12)' : 'transparent',
                    }}
                  >
                    <Sun className={`w-4 h-4 ${themePreference === 'light' ? 'text-white/90' : 'text-white/30'}`} />
                  </button>
                  <button
                    onClick={() => setThemePreference('dark')}
                    className="flex-1 flex items-center justify-center py-2.5 rounded-xl transition-all"
                    style={{
                      background: themePreference === 'dark' ? 'rgba(255,255,255,0.12)' : 'transparent',
                    }}
                  >
                    <Moon className={`w-4 h-4 ${themePreference === 'dark' ? 'text-white/90' : 'text-white/30'}`} />
                  </button>
                </div>

                {/* Scroll / Swipe */}
                <div
                  className="flex items-center gap-1 p-1 rounded-2xl flex-1"
                  style={{ background: 'rgba(255,255,255,0.05)' }}
                >
                  <button
                    onClick={() => setNavigationMode('scroll')}
                    className="flex-1 flex items-center justify-center py-2.5 rounded-xl transition-all"
                    style={{
                      background: navigationMode === 'scroll' ? 'rgba(255,255,255,0.12)' : 'transparent',
                    }}
                  >
                    <Scroll className={`w-4 h-4 ${navigationMode === 'scroll' ? 'text-white/90' : 'text-white/30'}`} />
                  </button>
                  <button
                    onClick={() => setNavigationMode('swipe')}
                    className="flex-1 flex items-center justify-center py-2.5 rounded-xl transition-all"
                    style={{
                      background: navigationMode === 'swipe' ? 'rgba(255,255,255,0.12)' : 'transparent',
                    }}
                  >
                    <Hand className={`w-4 h-4 ${navigationMode === 'swipe' ? 'text-white/90' : 'text-white/30'}`} />
                  </button>
                </div>
              </div>

              {/* Sensitivity slider */}
              <div className="px-1">
                <input
                  type="range" min="0" max="1" step="0.1"
                  value={gestureSensitivity}
                  onChange={(e) => setGestureSensitivity(parseFloat(e.target.value))}
                  className="w-full h-1 rounded-full appearance-none cursor-pointer"
                  style={{ background: 'rgba(255,255,255,0.1)' }}
                />
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed z-50 w-14 h-14 rounded-full flex items-center justify-center"
        style={{
          bottom: '24px',
          left: '50%',
          translateX: '-50%',
          transform: 'translateX(-50%)',
          background: 'rgba(255,255,255,0.10)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.14)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
        }}
        whileTap={{ scale: 0.88 }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.18 }}
            >
              <X className="w-5 h-5 text-white/80" />
            </motion.div>
          ) : (
            <motion.div
              key="dot"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="flex flex-col gap-[4px] items-center"
            >
              <div className="w-1 h-1 rounded-full bg-white/60" />
              <div className="w-1 h-1 rounded-full bg-white/60" />
              <div className="w-1 h-1 rounded-full bg-white/60" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </>
  );
}
